/**
 * @file components/settings/ProviderIcon.jsx
 * @description A component that dynamically displays an SVG icon for a given provider ID.
 */

import React, { useState, useEffect } from 'react';
import { Key } from 'lucide-react'; // Using Key as a relevant fallback icon

// In-memory cache to prevent re-fetching the same icon asset
const iconCache = new Map();

const ProviderIcon = ({ providerId, size = 24 }) => {
    const [iconSrc, setIconSrc] = useState(null);

    useEffect(() => {
        if (!providerId) return;

        // 1. Check the cache first
        if (iconCache.has(providerId)) {
            setIconSrc(iconCache.get(providerId));
            return;
        }

        // 2. If not cached, try to load the icon dynamically
        const loadIcon = async () => {
            try {
                // The providerId ('openai', 'anthropic', 'google') directly maps to the filename
                const { default: src } = await import(`../../assets/${providerId}.svg`);
                
                setIconSrc(src);
                iconCache.set(providerId, src); // Cache the successful result

            } catch (error) {
                console.warn(`Icon for provider ${providerId}.svg not found. Using fallback.`);
                setIconSrc('fallback');
                iconCache.set(providerId, 'fallback'); // Cache the fallback result
            }
        };

        loadIcon();
    }, [providerId]); // Re-run only when providerId changes


    // --- Render Logic ---
    if (iconSrc === 'fallback') {
        return <Key size={size} />; // Render fallback icon on failure
    }

    if (iconSrc) {
        // Render the custom SVG icon
        return <img src={iconSrc} alt={`${providerId} logo`} width={size} height={size} />;
    }

    // Render fallback icon while loading to prevent layout shift
    return <Key size={size} />;
};

export default ProviderIcon;